import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { ADJUSTMENT_REASONS, type CountView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Select,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextArea,
  TextField,
  Section,
} from '@wodi/ui';
import { useState } from 'react';

import { TableScroll } from '../layout/screen.tsx';
import { approveCount, countQuery, decideCount, saveCountLine, submitCount } from '../api/stock.ts';
import { useOnline } from '../hooks/use-online.ts';
import { formatIsoDate } from '../patients/format.ts';
import { BackToStore, errorText, WithStore } from '../stock/common.tsx';
import {
  formatQuantity,
  readQuantity,
  REASON_LABELS,
  STATUS_LABELS,
  STATUS_TAGS,
} from '../stock/format.ts';
import styles from '../stock/stock.module.css';
import { toasts } from '../toasts.ts';

const route = getRouteApi('/_app/stores/$storeId/counts/$countId');

/**
 * A stock count (plan §5.6, decision 9). While counting, the sheet lists what the store
 * should hold without saying how much; each line is saved as it is counted. After
 * submission, someone else reviews the differences and approves them with reasons.
 */
export function StockCountPage() {
  const { storeId, countId } = route.useParams();
  const count = useQuery(countQuery(countId));
  return (
    <WithStore storeId={storeId}>
      {(store) => (
        <div className={styles.page}>
          <BackToStore storeId={store.id} name={store.name} />
          {count.isPending ? <LoadingBlock label="Loading count" rows={4} /> : null}
          {count.isError ? (
            <Notice
              tone="error"
              action={
                <Button dense onPress={() => void count.refetch()}>
                  Try again
                </Button>
              }
            >
              {errorText(count.error, "This count couldn't be loaded. Try again.")}
            </Notice>
          ) : null}
          {count.isSuccess ? <CountBody count={count.data} storeName={store.name} /> : null}
        </div>
      )}
    </WithStore>
  );
}

function CountBody({ count, storeName }: { count: CountView; storeName: string }) {
  return (
    <>
      <PageHeader title={`Count ${count.number} in ${storeName}`} />
      <p>
        <Tag state={STATUS_TAGS[count.status]}>{STATUS_LABELS[count.status]}</Tag> Started{' '}
        {formatIsoDate(count.startedAt.slice(0, 10))}.
      </p>
      {count.status === 'in_progress' ? <CountSheet count={count} /> : <Review count={count} />}
    </>
  );
}

function useSetCount(count: CountView) {
  const queryClient = useQueryClient();
  return (next: CountView) => {
    queryClient.setQueryData(countQuery(count.id).queryKey, next);
  };
}

function CountSheet({ count }: { count: CountView }) {
  const online = useOnline();
  const setCount = useSetCount(count);
  const queryClient = useQueryClient();
  const submit = useMutation({
    mutationFn: () => submitCount(count.id),
    onSuccess: async (next) => {
      setCount(next);
      await queryClient.invalidateQueries({ queryKey: ['stock', count.storeId] });
      toasts.show({ title: 'Count submitted', description: count.number });
    },
  });
  return (
    <>
      <p>Count what is on the shelf and enter it. The expected quantity isn't shown.</p>
      <Section title="To count">
        {count.toCount.length === 0 ? (
          <p className={styles.meta}>Every batch has been counted.</p>
        ) : (
          count.toCount.map((b) => <CountEntry key={b.batchId} count={count} batch={b} />)
        )}
      </Section>
      <Section title="Counted">
        <TableScroll>
          <Table flat aria-label="Counted batches" dense>
            <TableHeader>
              <Column isRowHeader>Item</Column>
              <Column>Batch</Column>
              <Column numeric>Counted</Column>
            </TableHeader>
            <TableBody
              items={count.lines.map((l) => ({ ...l, id: l.batchId }))}
              renderEmptyState={() => 'Nothing counted yet.'}
            >
              {(l) => (
                <Row id={l.batchId}>
                  <Cell>{l.itemName}</Cell>
                  <Cell>
                    <span className="wodi-id">{l.batchNumber}</span>
                  </Cell>
                  <Cell numeric>{formatQuantity(l.countedQuantity, l.baseUnitDisplay)}</Cell>
                </Row>
              )}
            </TableBody>
          </Table>
        </TableScroll>
      </Section>
      {submit.error === null ? null : (
        <Notice tone="error">
          {errorText(submit.error, "The count wasn't submitted. Try again.")}
        </Notice>
      )}
      <div className={styles.actions}>
        <Button
          variant="primary"
          isDisabled={!online || count.lines.length === 0}
          isPending={submit.isPending}
          pendingLabel="Submitting…"
          onPress={() => {
            submit.mutate();
          }}
        >
          Submit count
        </Button>
      </div>
    </>
  );
}

function CountEntry({ count, batch }: { count: CountView; batch: CountView['toCount'][number] }) {
  const online = useOnline();
  const setCount = useSetCount(count);
  const [text, setText] = useState('');
  const value = readQuantity(text);
  const save = useMutation({
    mutationFn: () =>
      saveCountLine(count.id, {
        itemId: batch.itemId,
        batch: { batchId: batch.batchId },
        countedQuantity: value ?? 0,
      }),
    onSuccess: setCount,
  });
  return (
    <form
      className={styles.line}
      onSubmit={(e) => {
        e.preventDefault();
        if (value !== null) save.mutate();
      }}
    >
      <p className={styles.lineHeading}>
        <span>
          {batch.itemName}, batch <span className="wodi-id">{batch.batchNumber}</span>
        </span>
        <span className={styles.meta}>
          {batch.expiryDate === null ? 'No expiry' : `Expires ${formatIsoDate(batch.expiryDate)}`}
        </span>
      </p>
      <div className={styles.row}>
        <TextField
          label="Counted"
          unit={batch.baseUnitDisplay}
          inputMode="numeric"
          value={text}
          onChange={setText}
          {...(text !== '' && value === null
            ? { errorMessage: 'Enter a whole number, 0 if none.' }
            : {})}
        />
      </div>
      {save.error === null ? null : (
        <Notice tone="error">{errorText(save.error, "That line wasn't saved. Try again.")}</Notice>
      )}
      <div className={styles.actions}>
        <Button
          type="submit"
          isDisabled={!online || value === null}
          isPending={save.isPending}
          pendingLabel="Saving…"
        >
          Save count
        </Button>
      </div>
    </form>
  );
}

function Review({ count }: { count: CountView }) {
  const online = useOnline();
  const setCount = useSetCount(count);
  const queryClient = useQueryClient();
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [decisionReason, setDecisionReason] = useState('');
  const differing = count.lines.filter((l) => l.variance !== null && l.variance !== 0);
  const ready =
    decisionReason.trim().length >= 5 && differing.every((l) => reasons[l.batchId] !== undefined);
  const approve = useMutation({
    mutationFn: () =>
      approveCount(count.id, {
        reason: decisionReason.trim(),
        varianceReasons: Object.fromEntries(
          differing.map((l) => [
            l.batchId,
            { reasonCode: reasons[l.batchId] as (typeof ADJUSTMENT_REASONS)[number] },
          ]),
        ),
      }),
    onSuccess: async (next) => {
      setCount(next);
      await queryClient.invalidateQueries({ queryKey: ['stock', count.storeId] });
      toasts.show({ title: 'Count approved', description: count.number });
    },
  });
  const reject = useMutation({
    mutationFn: () => decideCount(count.id, 'reject', decisionReason.trim()),
    onSuccess: setCount,
  });
  return (
    <>
      <TableScroll>
        <Table aria-label="Counted against the stock record" dense>
          <TableHeader>
            <Column isRowHeader>Item</Column>
            <Column>Batch</Column>
            <Column numeric>Expected</Column>
            <Column numeric>Counted</Column>
            <Column numeric>Difference</Column>
            <Column>Reason</Column>
          </TableHeader>
          <TableBody items={count.lines.map((l) => ({ ...l, id: l.batchId }))}>
            {(l) => (
              <Row id={l.batchId}>
                <Cell>{l.itemName}</Cell>
                <Cell>
                  <span className="wodi-id">{l.batchNumber}</span>
                </Cell>
                <Cell numeric>{l.systemQuantity === null ? '' : String(l.systemQuantity)}</Cell>
                <Cell numeric>{String(l.countedQuantity)}</Cell>
                <Cell numeric>
                  {l.variance === null || l.variance === 0 ? '' : String(l.variance)}
                </Cell>
                <Cell>
                  {l.varianceReasonCode !== null ? (
                    REASON_LABELS[l.varianceReasonCode]
                  ) : l.variance === 0 || !count.canDecide ? (
                    ''
                  ) : (
                    <Select
                      label={`Reason for ${l.batchNumber}`}
                      dense
                      placeholder="Choose"
                      options={ADJUSTMENT_REASONS.filter(
                        (r) => r !== 'other' && r !== 'theft_suspected',
                      ).map((r) => ({ id: r, label: REASON_LABELS[r] }))}
                      value={reasons[l.batchId] ?? null}
                      onChange={(v) => {
                        setReasons((all) => ({ ...all, [l.batchId]: String(v) }));
                      }}
                    />
                  )}
                </Cell>
              </Row>
            )}
          </TableBody>
        </Table>
      </TableScroll>
      {count.canDecide ? (
        <div className={styles.form}>
          <TextArea
            label="Reason for your decision"
            description="At least five characters."
            value={decisionReason}
            onChange={setDecisionReason}
          />
          {[approve.error, reject.error].map((e, i) =>
            e === null ? null : (
              <Notice key={i} tone="error">
                {errorText(e, "That wasn't saved. Try again.")}
              </Notice>
            ),
          )}
          <div className={styles.actions}>
            <Button
              variant="primary"
              isDisabled={!online || !ready}
              isPending={approve.isPending}
              pendingLabel="Approving…"
              onPress={() => {
                approve.mutate();
              }}
            >
              Approve count
            </Button>
            <Button
              isDisabled={!online || decisionReason.trim().length < 5}
              isPending={reject.isPending}
              pendingLabel="Rejecting…"
              onPress={() => {
                reject.mutate();
              }}
            >
              Reject count
            </Button>
          </div>
        </div>
      ) : count.status === 'submitted' ? (
        <p className={styles.meta}>Another person with approval rights must approve this count.</p>
      ) : count.decisionReason === null ? null : (
        <p className={styles.meta}>Decision: {count.decisionReason}</p>
      )}
    </>
  );
}
